import React, { useCallback, useEffect, useState } from 'react';
import {
  archivesApi, ARCHIVE_TYPES, ARCHIVE_LANGUAGES,
  TYPE_LABEL, TYPE_GLYPH, LANGUAGE_LABEL,
} from '../../services/archivesApi';
import { getToken } from '../auth';
import '../styles/Archives.css';
import './admin-form.css';

const EMPTY_FORM = {
  title: '', slug: '',
  type: 'manuscrit', collection: '', year: '',
  author: '', contributor: '',
  summary: '', transcription: '',
  fileUrl: '', fileSize: '', previewUrl: '',
  duration: 0, pageCount: 0, language: 'na',
  tagsText: '', // saisie comma-separated
  imageSeed: '', imageUrl: '',
  featured: false, published: true,
  order: 0,
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}
function tagsToText(arr) { return Array.isArray(arr) ? arr.join(', ') : ''; }

const Archives = () => {
  const token = getToken();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterLanguage, setFilterLanguage] = useState('all');
  const [query, setQuery] = useState('');

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await archivesApi.adminList(token, {
        type: filterType !== 'all' ? filterType : undefined,
        language: filterLanguage !== 'all' ? filterLanguage : undefined,
        q: query || undefined,
        limit: 200,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, filterType, filterLanguage, query]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditingId(null); setForm(EMPTY_FORM); setSaveError(null); setOpen(true); };

  const openEdit = async (id) => {
    setEditingId(id);
    setSaveError(null);
    try {
      const { archive } = await archivesApi.adminGet(token, id);
      setForm({
        title: archive.title,
        slug: archive.slug,
        type: archive.type,
        collection: archive.collection || '',
        year: archive.year || '',
        author: archive.author || '',
        contributor: archive.contributor || '',
        summary: archive.summary || '',
        transcription: archive.transcription || '',
        fileUrl: archive.fileUrl || '',
        fileSize: archive.fileSize || '',
        previewUrl: archive.previewUrl || '',
        duration: archive.duration || 0,
        pageCount: archive.pageCount || 0,
        language: archive.language,
        tagsText: tagsToText(archive.tags),
        imageSeed: archive.imageSeed || '',
        imageUrl: archive.imageUrl || '',
        featured: archive.featured,
        published: archive.published,
        order: archive.order || 0,
      });
      setOpen(true);
    } catch (err) {
      alert("Impossible de charger l'archive : " + err.message);
    }
  };

  const close = useCallback(() => {
    setOpen(false); setEditingId(null); setForm(EMPTY_FORM); setSaveError(null);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = ''; window.removeEventListener('keydown', onKey); };
  }, [open, close]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);
    try {
      const tags = (form.tagsText || '').split(',').map((s) => s.trim()).filter(Boolean);
      const payload = {
        title: form.title,
        slug: form.slug || undefined,
        type: form.type,
        collection: form.collection,
        year: form.year ? parseInt(form.year, 10) : null,
        author: form.author,
        contributor: form.contributor,
        summary: form.summary,
        transcription: form.transcription,
        fileUrl: form.fileUrl,
        fileSize: form.fileSize,
        previewUrl: form.previewUrl,
        duration: parseInt(form.duration, 10) || 0,
        pageCount: parseInt(form.pageCount, 10) || 0,
        language: form.language,
        tags,
        imageSeed: form.imageSeed,
        imageUrl: form.imageUrl,
        featured: form.featured,
        published: form.published,
        order: parseInt(form.order, 10) || 0,
      };
      if (editingId) await archivesApi.adminUpdate(token, editingId, payload);
      else           await archivesApi.adminCreate(token, payload);
      close();
      await load();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id, title) => {
    if (!window.confirm(`Supprimer définitivement « ${title} » ?`)) return;
    try {
      await archivesApi.adminDelete(token, id);
      await load();
    } catch (err) {
      alert('Suppression impossible : ' + err.message);
    }
  };

  const onToggleFeatured  = async (id, v) => { try { await archivesApi.adminUpdate(token, id, { featured:  !v }); load(); } catch (e) { alert(e.message); } };
  const onTogglePublished = async (id, v) => { try { await archivesApi.adminUpdate(token, id, { published: !v }); load(); } catch (e) { alert(e.message); } };

  if (!token) {
    return (
      <div className="content" style={{ padding: 40 }}>
        <p style={{ fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '.18em', color: 'var(--char-500)' }}>
          Session expirée. Reconnectez-vous.
        </p>
      </div>
    );
  }

  const imgFor = (a) => a.imageUrl || `https://picsum.photos/seed/${a.imageSeed || `cb-arc-${a.id}`}/200/150`;
  const typePillClass = (t) => {
    if (t === 'audio') return 'sunset';
    if (t === 'manuscrit' || t === 'texte') return 'gold';
    if (t === 'photo' || t === 'carte') return 'ocean';
    return '';
  };

  // Quel champ "principal" afficher selon le type
  const sizeLabel = (a) => {
    if (a.duration > 0) {
      const m = Math.floor(a.duration / 60);
      const s = a.duration % 60;
      return `${m}:${String(s).padStart(2, '0')}`;
    }
    if (a.pageCount > 0) return `${a.pageCount} p.`;
    if (a.fileSize) return a.fileSize;
    return '—';
  };

  return (
    <>
      <div className="topbar">
        <div className="breadcrumb">
          <span>Cambérène</span><span className="sep">/</span>
          <span>Admin</span><span className="sep">/</span>
          <b>Archives</b>
        </div>
        <div className="search">
          <span className="ic" />
          <input
            type="text"
            placeholder="Chercher une archive, un auteur, un tag…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <kbd>⌘ K</kbd>
        </div>
        <div className="top-actions">
          <button className="icon-btn" title="Rafraîchir" onClick={load} aria-label="Rafraîchir">↻</button>
          <button className="top-cta" onClick={openCreate}>
            <span className="plus" /> Nouvelle archive
          </button>
        </div>
      </div>

      <div className="content">
        <div className="page-head">
          <div>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.22em', textTransform: 'uppercase', color: 'var(--char-500)' }}>
              Bibliothèque · {total} document{total > 1 ? 's' : ''}
            </span>
            <h1>Les archives,<br /><span style={{ fontStyle: 'italic' }}>en gestion.</span></h1>
            <p className="sub">Manuscrits, photos, audio, vidéos, cartes, textes — toutes les archives Layène. Synchronisé avec /bibliotheque.</p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="pill-btn"
              style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: '7px 14px' }}
            >
              {ARCHIVE_TYPES.map((t) => <option key={t.id} value={t.id}>{t.lbl}</option>)}
            </select>
            <select
              value={filterLanguage}
              onChange={(e) => setFilterLanguage(e.target.value)}
              className="pill-btn"
              style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: '7px 14px' }}
            >
              {ARCHIVE_LANGUAGES.map((l) => <option key={l.id} value={l.id}>{l.lbl}</option>)}
            </select>
          </div>
        </div>

        {error && (
          <div className="bulkbar on" style={{ background: 'rgba(230,57,70,.12)', color: '#B72333' }}>
            <span>⚠</span><span>Erreur : {error}</span>
          </div>
        )}

        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 80 }} />
                <th>Titre</th>
                <th>Type</th>
                <th>Collection</th>
                <th>Année</th>
                <th>Lang.</th>
                <th>Taille</th>
                <th>Statut</th>
                <th style={{ width: 110 }} />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: '40px 0', color: 'var(--char-500)', fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '.18em', textTransform: 'uppercase' }}>Chargement…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: '40px 0', color: 'var(--char-500)' }}>
                  Aucune archive. Cliquez « Nouvelle archive » pour démarrer.
                </td></tr>
              )}
              {items.map((a) => (
                <tr key={a.id} onClick={() => openEdit(a.id)}>
                  <td onClick={(ev) => ev.stopPropagation()}>
                    <div
                      style={{
                        width: 56, height: 42, borderRadius: 8,
                        backgroundImage: `url('${imgFor(a)}')`,
                        backgroundSize: 'cover', backgroundPosition: 'center',
                      }}
                    />
                  </td>
                  <td>
                    <b style={{ fontWeight: 500, fontSize: 14 }}>
                      {a.title}
                      {a.featured && <span style={{ marginLeft: 6, color: 'var(--gold)' }}>★</span>}
                    </b>
                    {a.summary && (
                      <small style={{ display: 'block', marginTop: 3, fontSize: 12, color: 'var(--char-500)', maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {a.summary}
                      </small>
                    )}
                  </td>
                  <td>
                    <span className={`cat-pill ${typePillClass(a.type)}`}>
                      <span style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', marginRight: 4 }}>{TYPE_GLYPH[a.type]}</span>
                      {TYPE_LABEL[a.type]}
                    </span>
                  </td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--char-500)' }}>{a.collection || '—'}</td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--char-700)', fontWeight: 500 }}>{a.year || '—'}</td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 10.5, letterSpacing: '.06em', color: 'var(--char-500)' }}>
                    {a.language === 'na' ? '—' : a.language.toUpperCase()}
                  </td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--char-500)' }}>{sizeLabel(a)}</td>
                  <td onClick={(ev) => ev.stopPropagation()}>
                    <span
                      className={`status-pill ${a.published ? 'live' : 'muted'}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => onTogglePublished(a.id, a.published)}
                    >
                      {a.published ? 'Publié' : 'Caché'}
                    </span>
                  </td>
                  <td onClick={(ev) => ev.stopPropagation()}>
                    <div className="row-actions" style={{ opacity: 1 }}>
                      <button
                        title={a.featured ? 'Retirer trésor' : 'Mettre en trésor'}
                        onClick={() => onToggleFeatured(a.id, a.featured)}
                        style={{
                          background: a.featured ? 'var(--gold)' : undefined,
                          color: a.featured ? 'var(--char-900)' : undefined,
                          borderColor: a.featured ? 'var(--gold)' : undefined,
                        }}
                      >★</button>
                      <button title="Éditer" onClick={() => openEdit(a.id)}>✎</button>
                      <button title="Supprimer" className="danger" onClick={() => onDelete(a.id, a.title)}>✕</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="table-foot">
            <span>{total} document{total > 1 ? 's' : ''}</span>
            <span>{items.length} affiché{items.length > 1 ? 's' : ''}</span>
          </div>
        </div>
      </div>

      {/* ============ MODALE ============ */}
      {open && (
        <div className="admin-modal-bg" onClick={close}>
          <div className="admin-modal" onClick={(ev) => ev.stopPropagation()}>
            <div className="admin-modal-head">
              <div>
                <span className="admin-modal-eyebrow">{editingId ? "Éditer l'archive" : 'Nouvelle archive'}</span>
                <h2 className="admin-modal-title">{editingId ? form.title || '—' : 'Ajouter un document'}</h2>
              </div>
              <button onClick={close} className="admin-modal-close" aria-label="Fermer">✕</button>
            </div>

            <form onSubmit={onSubmit} className="admin-form">
              <Field label="Titre du document">
                <input
                  type="text"
                  required
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="Ex : Manuscrits fondateurs de Seydina Limamou Laye"
                />
              </Field>

              <div className="admin-form-row" style={{ gridTemplateColumns: '1.5fr 1fr' }}>
                <Field label="Slug (URL · optionnel)">
                  <input
                    type="text"
                    value={form.slug}
                    onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                    placeholder="auto-généré depuis le titre"
                    pattern="[a-z0-9\-]*"
                  />
                </Field>
                <Field label="Type">
                  <select
                    required
                    value={form.type}
                    onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                  >
                    {ARCHIVE_TYPES.filter((t) => t.id !== 'all').map((t) =>
                      <option key={t.id} value={t.id}>{t.glyph} · {t.lbl}</option>
                    )}
                  </select>
                </Field>
              </div>

              <div className="admin-form-row">
                <Field label="Collection (texte libre)">
                  <input
                    type="text"
                    value={form.collection}
                    onChange={(e) => setForm((f) => ({ ...f, collection: e.target.value }))}
                    placeholder="Textes fondateurs, Audio tassou…"
                  />
                </Field>
                <Field label="Année (du document original)">
                  <input
                    type="number"
                    min={1800}
                    max={new Date().getFullYear()}
                    value={form.year}
                    onChange={(e) => setForm((f) => ({ ...f, year: e.target.value }))}
                    placeholder="1884"
                  />
                </Field>
              </div>

              <div className="admin-form-row">
                <Field label="Auteur original">
                  <input
                    type="text"
                    value={form.author}
                    onChange={(e) => setForm((f) => ({ ...f, author: e.target.value }))}
                    placeholder="Seydina Limamou Laye…"
                  />
                </Field>
                <Field label="Contributeur (qui a numérisé)">
                  <input
                    type="text"
                    value={form.contributor}
                    onChange={(e) => setForm((f) => ({ ...f, contributor: e.target.value }))}
                    placeholder="Archives layènes, Famille Diop…"
                  />
                </Field>
              </div>

              <Field label="Résumé (affiché sur la carte publique)">
                <textarea
                  rows={2}
                  maxLength={280}
                  value={form.summary}
                  onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))}
                  placeholder="Ce que contient le document, en deux phrases"
                />
              </Field>

              <Field label="Transcription / notes (page détail)">
                <textarea
                  rows={4}
                  value={form.transcription}
                  onChange={(e) => setForm((f) => ({ ...f, transcription: e.target.value }))}
                  placeholder="Transcription complète, notes du conservateur"
                />
              </Field>

              <div className="admin-form-row">
                <Field label="Langue principale">
                  <select
                    value={form.language}
                    onChange={(e) => setForm((f) => ({ ...f, language: e.target.value }))}
                  >
                    {ARCHIVE_LANGUAGES.filter((l) => l.id !== 'all').map((l) =>
                      <option key={l.id} value={l.id}>{l.lbl}</option>
                    )}
                  </select>
                </Field>
                <Field label="Tags (séparés par des virgules)">
                  <input
                    type="text"
                    value={form.tagsText}
                    onChange={(e) => setForm((f) => ({ ...f, tagsText: e.target.value }))}
                    placeholder="tassou, musique, 1960, wolof"
                  />
                </Field>
              </div>

              <div className="admin-form-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                <Field label="Durée (secondes · audio/vidéo)">
                  <input
                    type="number"
                    min={0}
                    value={form.duration}
                    onChange={(e) => setForm((f) => ({ ...f, duration: e.target.value }))}
                  />
                </Field>
                <Field label="Pages (textes/cartes)">
                  <input
                    type="number"
                    min={0}
                    value={form.pageCount}
                    onChange={(e) => setForm((f) => ({ ...f, pageCount: e.target.value }))}
                  />
                </Field>
                <Field label="Taille fichier (texte libre)">
                  <input
                    type="text"
                    value={form.fileSize}
                    onChange={(e) => setForm((f) => ({ ...f, fileSize: e.target.value }))}
                    placeholder="48 Mo · 2.4 Go"
                  />
                </Field>
              </div>

              <Field label="URL du fichier (PDF, MP3, MP4, ZIP…)">
                <input
                  type="url"
                  value={form.fileUrl}
                  onChange={(e) => setForm((f) => ({ ...f, fileUrl: e.target.value }))}
                  placeholder="https://archives.camberene.sn/files/…"
                />
              </Field>

              <Field label="URL de prévisualisation (extrait audio/vidéo · optionnel)">
                <input
                  type="url"
                  value={form.previewUrl}
                  onChange={(e) => setForm((f) => ({ ...f, previewUrl: e.target.value }))}
                  placeholder="https://archives.camberene.sn/preview/…"
                />
              </Field>

              <Field label="Image de couverture · seed Picsum ou URL">
                <input
                  type="text"
                  value={form.imageSeed || form.imageUrl}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (/^https?:\/\//.test(v)) setForm((f) => ({ ...f, imageUrl: v, imageSeed: '' }));
                    else setForm((f) => ({ ...f, imageSeed: v, imageUrl: '' }));
                  }}
                  placeholder="cb-archive-manuscrit-1884  ·  ou  https://…"
                />
              </Field>

              <div className="admin-form-row">
                <label className="admin-publish-toggle">
                  <input
                    type="checkbox"
                    checked={form.featured}
                    onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))}
                  />
                  <span>★ Trésor / mis en avant</span>
                </label>
                <label className="admin-publish-toggle">
                  <input
                    type="checkbox"
                    checked={form.published}
                    onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))}
                  />
                  <span>{form.published ? 'Visible sur la bibliothèque' : 'Caché (brouillon)'}</span>
                </label>
              </div>

              {saveError && <div className="admin-form-error">{saveError}</div>}

              <div className="admin-form-foot">
                <button type="button" onClick={close} className="admin-btn">Annuler</button>
                <button type="submit" disabled={saving} className="admin-btn admin-btn-primary">
                  {saving ? 'Enregistrement…' : (editingId ? 'Enregistrer' : 'Ajouter')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

function Field({ label, children }) {
  return (
    <label className="admin-field">
      <span className="admin-field-label">{label}</span>
      {children}
    </label>
  );
}

export default Archives;
